import { useElements } from '@stripe/react-stripe-js'

import useValidation from './useValidation'

// Shared validation for the donor's name / email / phone / address
// inputs. Used by the fiat Contact step and the unified crypto form
// where Contact lives on the same panel as the amount inputs.
// (DonationExtras opt-ins are each their own self-contained modal —
// committed state is always valid by the time it reaches saveDraft, so
// nothing extras-related needs to be re-validated here.)
//
// Fieldset is built conditionally:
// - firstName / lastName / phone live on the manual inputs only when
//   the AddressElement isn't taking over (display.name='split' on
//   AddressElement supplies these inline, so we skip the manual fields
//   to avoid duplicate required-field errors)
// - email is required only when session.collect_email is on; when off,
//   the logged-in user's email flows in via session on the backend
//
// `validate()` rolls the field check and the AddressElement
// completeness check into a single boolean. Async because
// AddressElement's getValue() returns a Promise.
const useContactValidation = ({ i18n, donation, session, collect_phone }) => {
  const elements = useElements()
  const validation = useValidation({
    i18n,
    fieldSet: (() => {
      const fields = {}
      if (!donation.collectAddress) {
        fields.firstName = { required: true, maxLength: 30 }
        fields.lastName  = { required: true, maxLength: 30 }
        if (collect_phone)
          fields.phone = { required: true }
      }
      if (session.collect_email)
        fields.email = { required: true, maxLength: 75, email: true }
      return fields
    })(),
  })

  const validate = async () => {
    const results = [ validation.validate(donation.state) ]
    if (donation.collectAddress) {
      const address = elements.getElement('address')
      const { complete } = await address.getValue()
      results.push(complete)
    }
    return results.every(Boolean)
  }

  return { validation, validate }
}

export default useContactValidation
